import { Either, Schema } from "effect";
import {
  LEGACY_PACK_FILES,
  LEGACY_PROFILE_MARKERS,
  legacyMarkerValue,
} from "./language-pack-topic-conversion.ts";
import {
  CATALOG_CHECK_IDS,
  CHECK_CATALOG,
  hasPackKey,
  isSamplelessCheck,
  readPackTopics,
  splitPackKeyRef,
  type TopicBlocker,
  topicFilePath,
} from "./language-pack-topics.ts";
import {
  activePersonas,
  PERSONAS_FILE,
  type Persona,
  PersonaId,
  type PersonaRole,
  type PersonasFile,
  parsePersonaImagePath,
  parsePersonasFile,
  renderPersonasFile,
} from "./personas.ts";

// ADR-0111 — "is this language pack complete enough to generate against, and if not, what exactly
// is missing?"
//
// One pure function, here in `core`, because the same answer is needed in three places that must
// never disagree: the upload form (before anything is written), the "Language taught" select in the
// New Curriculum modal (an L2 whose pack is incomplete must not be offered), and the server's own
// refusal to accept an upload. A second copy of these rules in the web app would drift the moment
// the pipeline's input check changes.
//
// The rules are derived from what actually fails a run, NOT from the prose in
// `L2__TEMPLATE__/README.md`:
//
//   * BLOCKER = `l2_unit_plan/scripts/run_pipeline.py::check_inputs` calls `sys.exit` on it. That is
//     the per-file existence list (`PERSONAS`, `REFERENCE_DIALOGUES`, `LANGUAGE_PROFILE` from the L2
//     pack; `L1_PROFILE`, `TUTORS` from the L1 pack) plus the registry, which both runtimes hard-exit
//     without. A blocker means the launch dies before a cluster does any work. ADR-0120 (amending
//     ADR-0111 Decision 2) adds the topic files: an invalid topic file, a missing or incomplete check
//     manifest, or an enabled check without its data is a blocker too — the same codes
//     `language_packs.pack_blockers` reports (`language-pack-topics.ts`). Missing data is never
//     replaced by a default, so it can no longer degrade to Spanish silently.
//   * WARNING = the run starts and degrades, usually silently — e.g. a pack that still keeps data in
//     a place no runtime reads any more (the pre-topic-file flat lists and profile markers).
//   * INFO = something this validator cannot decide and a human should know about, rather than
//     silence that reads as approval.
//
// ADR-0118 moves the persona registry into the pack (`personas.yml`), so the cast gate is no longer
// deferred to launch: every `## <Name>` in `personas.md` / `tutors.md` is resolved against the
// pack's own registry here, and an unknown name is a blocker at upload rather than a launch failure.
//
// Deliberately NOT a blocker: the vocab `.zip` and the grammar ledgers. The zip is binary and is not
// even a valid `LanguagePackPath`, and the ledgers are pipeline OUTPUT (step 0 generates them into
// `l2_unit_plan/generated/`), so requiring either at upload time would make every honest pack invalid.

export const PackFindingSeverity = Schema.Literal("blocker", "warning", "info");
export type PackFindingSeverity = typeof PackFindingSeverity.Type;

export const PackFinding = Schema.Struct({
  severity: PackFindingSeverity,
  // The pack-relative path the finding is about (`L2_FRA/personas.md`), or the registry, or absent
  // when the finding is about the upload as a whole.
  path: Schema.optional(Schema.String),
  // What is wrong, stated as a fact.
  message: Schema.String,
  // What to do about it. Every finding carries one — a report that says only "personas.md is
  // missing" makes the reader go and find the template themselves, which is the work this is
  // supposed to remove.
  fix: Schema.String,
});
export type PackFinding = typeof PackFinding.Type;

/** One file of an upload: its pack-relative path and its text. */
export const PackFile = Schema.Struct({
  path: Schema.String,
  content: Schema.String,
});
export type PackFile = typeof PackFile.Type;

export const PackReport = Schema.Struct({
  // Absent for a report on an L1 pack alone (`checkL1Pack`).
  l2: Schema.optional(Schema.String),
  l1: Schema.optional(Schema.String),
  findings: Schema.Array(PackFinding),
  // `true` when nothing is a blocker — the pack may be written, and a run may be launched against
  // it. Warnings never gate: the standing rule from the generation working session is that only
  // checks whose failure produces an unusable result block, everything else informs.
  complete: Schema.Boolean,
});
export type PackReport = typeof PackReport.Type;

const hasBlocker = (findings: readonly PackFinding[]): boolean =>
  findings.some((finding) => finding.severity === "blocker");

// `<!-- pronoun-noise: i, you, he, … -->` in the L1 profile. Absent, the match-exercise parser in
// `convert_unit_plan_md_to_json.py` silently falls back to the ENGLISH pronoun set — so this matters
// for any L1 that is not English, and is harmless for one that is. It is reported at one severity
// regardless, because this validator does not know which L1 is which.
const PRONOUN_NOISE_MARKER = /<!--\s*pronoun-noise:/;

// `dialog_polish.py` parses `personas.md` by H2, and excludes exactly these two headings from the
// persona set BY NAME. Rename either and its content is parsed as a persona named "Learner
// positioning" — which then fails the cast gate at launch with a confusing message.
export const REQUIRED_PERSONA_SECTIONS = [
  "## Learner positioning",
  "## Cast distribution rules",
] as const;

// By prefix: the committed packs title the second one `## Cast distribution rules (for the planner)`,
// and treating that as a persona would report it as a cast member missing every field.
export const isReservedSection = (heading: string): boolean =>
  REQUIRED_PERSONA_SECTIONS.some((section) => heading.startsWith(section));

// The six bold fields the template requires of every persona block. Missing ones do not fail a
// launch, they flatten the generated dialogue — the pack is structurally fine and the output is
// generic, which is the failure mode hardest to spot after the fact.
export const PERSONA_FIELDS = [
  "**Age / origin:**",
  "**Role:**",
  "**Register with learner:**",
  "**Voice / quirks:**",
  "**Regional / dialect features:**",
  "**Default scene contexts",
] as const;

/** The token every unfilled slot carries, so `checkLanguagePack` can find the ones left behind. */
export const FILL_MARKER = "<<FILL:";

// The marker `validate_tutors_md` keys on when it collects tutor names.
const TUTOR_VOICE_MARKER = "**Voice / tone:**";

const h2Headings = (content: string): readonly string[] =>
  content
    .split("\n")
    .filter((line) => line.startsWith("## "))
    .map((line) => line.trim());

/** The `## <Name>` blocks that are cast entries — everything but the two reserved section titles. */
const castNames = (content: string): readonly string[] =>
  h2Headings(content)
    .filter((heading) => !isReservedSection(heading))
    .map((heading) => heading.slice(3).trim());

/** Split a file on its `## ` headings, so a per-persona check sees only that persona's block. */
const blocksOf = (content: string): readonly { readonly name: string; readonly body: string }[] => {
  const blocks: { name: string; body: string }[] = [];
  for (const line of content.split("\n")) {
    if (line.startsWith("## ")) {
      blocks.push({ name: line.slice(3).trim(), body: "" });
      continue;
    }
    const current = blocks.at(-1);
    if (current !== undefined) current.body += `${line}\n`;
  }
  return blocks;
};

const registryHasCode = (registry: string, code: string): boolean =>
  registry.split("\n").some((line) => line.trim().startsWith(`${code}:`));

// `@${VOCAB_INVENTORY}` is inlined unconditionally by the step-1 prompts, so a pack with no vocab
// reference fails at render time with `FileNotFoundError` — after the cluster has started, which is
// why it is a warning here and not a launch blocker. The deployed tree may still carry a `.yml.zip`
// twin (binary, never stored in studio), which `run_pipeline.py` prefers when present.
const VOCAB_INVENTORY = "vocab/full_vocab_inventory.reference.yml";

export interface PackCompletenessInput {
  /** The L2 code WITHOUT its `L2_` prefix, e.g. `FRA`. */
  readonly l2: string;
  /** The L1 code without its `L1_` prefix. Omitted when only the L2 side is being judged. */
  readonly l1?: string | undefined;
  /**
   * Every pack file that WILL exist once this upload lands: the uploaded files merged over whatever
   * the store already holds. The caller does the merging, so this function answers the same question
   * for an upload preview and for a pack already in the store without two code paths.
   */
  readonly files: readonly PackFile[];
  /**
   * Every image path (`L2_FRA/personas/<id>/<slot>.webp`) that WILL exist once this upload lands.
   * Omitted when the caller does not know (the readiness list, which only needs blockers): image
   * presence is then not judged at all rather than reported missing. Images only ever warn.
   */
  readonly images?: readonly string[] | undefined;
}

const EDITOR = "in studio's Language packs page";

/** `lexicon.function_words` -> "`function_words` in `lexicon.yml`". */
const keyInFile = (ref: `${string}.${string}`): string => {
  const dot = ref.indexOf(".");
  return `\`${ref.slice(dot + 1)}\` in \`${ref.slice(0, dot)}.yml\``;
};

// One finding per `pack_blockers` code, naming the file to change and what to put in it.
const topicFinding = (l2: string, blocker: TopicBlocker): PackFinding => {
  const checksPath = topicFilePath(l2, "checks");
  switch (blocker._tag) {
    case "Invalid":
      return {
        severity: "blocker",
        path: topicFilePath(l2, blocker.topic),
        message: `\`${blocker.topic}.yml\` does not match its schema: ${blocker.issues.slice(0, 5).join("; ")}.`,
        fix: `Fix it ${EDITOR} so it holds only the keys \`${blocker.topic}.schema.json\` lists, each with a value of the right type — no list, string or mapping may be empty. Leave out a key you have no data for.`,
      };
    case "AbsentManifest":
      return {
        severity: "blocker",
        path: checksPath,
        message: "The check manifest `checks.yml` is missing.",
        fix: `Add \`checks.yml\` ${EDITOR}, setting every catalog check (${CATALOG_CHECK_IDS.map((id) => `\`${id}\``).join(", ")}) to \`enabled: true\` or \`enabled: false\`. There are no defaults; the template lists them all.`,
      };
    case "Undecided": {
      const check = CHECK_CATALOG[blocker.checkId];
      return {
        severity: "blocker",
        path: checksPath,
        message: `\`checks.yml\` does not decide \`${blocker.checkId}\`.`,
        fix: `Add \`${blocker.checkId}: { enabled: true }\` or \`{ enabled: false }\` under \`checks:\` ${EDITOR}. The check: ${check.description} Enabled, it needs ${check.requires.map(keyInFile).join(" and ")}.`,
      };
    }
    case "Unknown":
      return {
        severity: "blocker",
        path: checksPath,
        message: `\`checks.yml\` decides \`${blocker.checkId}\`, which is not a catalog check.`,
        fix: `Remove it ${EDITOR}, or correct the id to one of ${CATALOG_CHECK_IDS.map((id) => `\`${id}\``).join(", ")}.`,
      };
    case "Missing":
      return {
        severity: "blocker",
        path: topicFilePath(l2, splitPackKeyRef(blocker.ref)[0]),
        message: `\`${blocker.checkId}\` is enabled, but ${keyInFile(blocker.ref)} is missing.`,
        fix: `Add ${keyInFile(blocker.ref)} ${EDITOR}, or set \`${blocker.checkId}: { enabled: false }\` in \`checks.yml\`. Missing pack data is never replaced by a default.`,
      };
  }
};

/**
 * Judge one (L2, L1?) pair's pack against what the pipeline requires of it.
 *
 * Pure and total: it never reads the store, the filesystem or the network, so the upload form can
 * run it on every change and the server can run the identical rules before writing.
 */
export const checkLanguagePack = (input: PackCompletenessInput): PackReport =>
  judgePack(input, { placeholdersBlock: false });

/**
 * Judge an L1 pack on its own — the learner's-language side of `checkLanguagePack`, with no L2.
 *
 * An L1 pack is shared by every L2 its audience learns, so it is created, edited and removed as a
 * thing of its own; this is the report for that, and the per-L1 half of `languageReadiness`. The
 * cross-pack rule (a persona id may not live in both an L1 and an L2 registry) needs a pair, so it
 * is judged only by `checkLanguagePack`.
 *
 * Unlike the pair check, a template placeholder left in the L1's files is a BLOCKER here. An L1 has
 * no undecided `checks.yml` to hold a fresh skeleton back, and its placeholders are what a run
 * speaks: a tutor named `<<FILL: first name of tutor 1>>`, voiced by a voice id that does not exist.
 * A freshly created L1 is therefore never offered in the New Curriculum modal until it is written.
 */
export const checkL1Pack = (
  input: Omit<PackCompletenessInput, "l2" | "l1"> & { readonly l1: string },
): PackReport => judgePack(input, { placeholdersBlock: true });

const judgePack = (
  input: Omit<PackCompletenessInput, "l2"> & { readonly l2?: string | undefined },
  options: { readonly placeholdersBlock: boolean },
): PackReport => {
  const findings: PackFinding[] = [];
  const byPath = new Map(input.files.map((file) => [file.path, file.content]));
  const l2Dir = input.l2 === undefined ? undefined : `L2_${input.l2}`;
  const l1Dir = input.l1 === undefined ? undefined : `L1_${input.l1}`;

  const requireFile = (path: string, what: string, fix: string): string | undefined => {
    const content = byPath.get(path);
    if (content === undefined) {
      findings.push({ severity: "blocker", path, message: `${what} is missing.`, fix });
      return undefined;
    }
    if (content.trim().length === 0) {
      findings.push({ severity: "blocker", path, message: `${what} is empty.`, fix });
      return undefined;
    }
    return content;
  };

  const imagesByPersona = new Map<string, Set<string>>();
  for (const image of input.images ?? []) {
    const parts = parsePersonaImagePath(image);
    if (parts === undefined) continue;
    const key = `${parts.packDir}/${parts.personaId}`;
    imagesByPersona.set(key, (imagesByPersona.get(key) ?? new Set()).add(parts.slot));
  }

  // A pack's `personas.yml`, parsed. Missing, empty or unparsable is a blocker: every runtime that
  // voices or draws a persona reads it, and none of them falls back (ADR-0118 Decision 7).
  const readRegistry = (path: string, what: string): PersonasFile | undefined => {
    const content = requireFile(
      path,
      `The ${what} registry \`${PERSONAS_FILE}\``,
      "Download the template for this language: it carries the file with a slot per persona. Or add the personas in the Personas view.",
    );
    if (content === undefined) return undefined;
    const parsed = parsePersonasFile(content);
    if (Either.isLeft(parsed)) {
      for (const issue of parsed.left.slice(0, 10)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${PERSONAS_FILE}\` does not parse: ${issue}.`,
          fix: "Fix the entry so it matches the shape described in the file's header comment.",
        });
      }
      return undefined;
    }
    return parsed.right;
  };

  const judgeRegistry = (packDir: string, file: PersonasFile, roles: readonly PersonaRole[]) => {
    const path = `${packDir}/${PERSONAS_FILE}`;
    const ids = new Set<string>();
    for (const persona of file.personas) {
      if (ids.has(persona.id)) {
        findings.push({
          severity: "blocker",
          path,
          message: `Persona id \`${persona.id}\` is defined twice.`,
          fix: "Keep one entry per id — the runtimes look a persona up by id, so a duplicate makes the choice arbitrary.",
        });
      }
      ids.add(persona.id);
    }
    // The runtimes match a dialogue speaker to a current persona by name or alias, ignoring case, so
    // a name two current personas share voices one of them arbitrarily. `persona_config` refuses
    // such a pack too.
    const named = new Map<string, string>();
    const sameName = (name: string) => name.normalize("NFC").trim().toLowerCase();
    for (const persona of file.personas) {
      if (persona.deprecated) continue;
      for (const name of new Set([persona.display_name, ...(persona.aliases ?? [])])) {
        const other = named.get(sameName(name));
        if (other !== undefined && other !== persona.id) {
          findings.push({
            severity: "blocker",
            path,
            message: `\`${name}\` names both \`${other}\` and \`${persona.id}\`.`,
            fix: "Give each current persona its own display name and aliases — a dialogue speaker with a shared name could be voiced and drawn as either.",
          });
        }
        named.set(sameName(name), persona.id);
      }
    }
    for (const persona of file.personas) {
      if (persona.alias_of !== undefined && !ids.has(persona.alias_of)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${persona.id}\` is an alias of \`${persona.alias_of}\`, which this file does not define.`,
          fix: "Point `alias_of` at a persona defined in the same file, or remove it.",
        });
      }
      if (persona.deprecated) continue;
      if (!roles.includes(persona.role)) {
        findings.push({
          severity: "warning",
          path,
          message: `\`${persona.id}\` has role \`${persona.role}\`, which this pack does not hold.`,
          fix: packDir.startsWith("L1_")
            ? "An L1 pack holds the tutors, who speak the L1. Move scene characters to the L2 pack's registry."
            : "Tutors speak the learner's L1, so they belong in the L1 pack's registry, not the L2's.",
        });
      }
      // A placeholder name or voice is not a gap but a value that fails: the conversion resolves
      // speakers by `display_name`, and the TTS Lambda sends `voice_id` to ElevenLabs as it stands,
      // which answers a placeholder with a 4xx. So both are blockers, where an absent voice — a
      // persona that may never speak — stays a warning.
      if (persona.display_name.includes(FILL_MARKER)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${persona.id}\` still has a placeholder name.`,
          fix: "Give the persona its first name — the name the curriculum's dialogues use for this speaker.",
        });
      }
      if (persona.voice === undefined) {
        findings.push({
          severity: "warning",
          path,
          message: `\`${persona.id}\` has no voice.`,
          fix: "Add `voice: { provider: elevenlabs, voice_id: <id>, speed: 1 }`. Without one, audio for any line this persona speaks fails at synthesis.",
        });
      } else if (persona.voice.voice_id.includes(FILL_MARKER)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${persona.id}\` still has a placeholder voice id.`,
          fix: "Set `voice.voice_id` to the ElevenLabs voice id this persona speaks with.",
        });
      }
      if (
        input.images !== undefined &&
        !imagesByPersona.get(`${packDir}/${persona.id}`)?.has("origin")
      ) {
        findings.push({
          severity: "warning",
          path: `${packDir}/personas/${persona.id}/`,
          message: `\`${persona.id}\` has no \`origin\` image.`,
          fix: "Add `personas/<id>/origin.webp` (or .png/.jpg). It is the identity reference sent to the image model; without it every picture of this persona is drawn from the description alone and will not look like the same person twice.",
        });
      }
    }
    // The default narrator voices and draws media that names no persona. Enrichment refuses a cast
    // without exactly one (`persona_config.default_narrator`), so so does the check.
    const narrators = file.personas.filter((persona) => persona.default_narrator);
    for (const persona of narrators) {
      if (persona.role !== "main-character" || persona.deprecated) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`${persona.id}\` is marked \`default_narrator\` but is not a current main character.`,
          fix: "Only a non-deprecated `main-character` of the L2 pack can be the default narrator. Remove `default_narrator` from this persona.",
        });
      }
    }
    if (!roles.includes("main-character")) return;
    const current = narrators.filter((p) => p.role === "main-character" && !p.deprecated);
    if (current.length !== 1) {
      findings.push({
        severity: "blocker",
        path,
        message:
          current.length === 0
            ? "No persona is the default narrator."
            : `${current.map((p) => `\`${p.id}\``).join(", ")} are all marked \`default_narrator\`.`,
        fix: "Mark exactly one current main character `default_narrator: true` (Personas view → Default narrator). It voices audio and is drawn in images whose media names no persona.",
      });
    }
  };

  // Every `## <Name>` a prompt file declares must resolve to an active persona of the right role —
  // the check `validate_personas_md` / `validate_tutors_md` used to run against the Python registry
  // at launch.
  const gateNames = (
    names: readonly string[],
    path: string,
    file: PersonasFile,
    role: PersonaRole,
    registryPath: string,
  ) => {
    const known = activePersonas(file, role).map((persona) => persona.display_name);
    for (const name of names) {
      if (!known.includes(name)) {
        findings.push({
          severity: "blocker",
          path,
          message: `\`## ${name}\` is not an active \`${role}\` in \`${registryPath}\`.`,
          fix:
            known.length === 0
              ? `Add a \`${role}\` persona with display_name \`${name}\` to the registry.`
              : `Use one of ${known.map((k) => `\`${k}\``).join(", ")}, or add \`${name}\` to the registry as a \`${role}\`.`,
        });
      }
    }
  };

  // ── The registry ────────────────────────────────────────────────────────────
  // Both runtimes resolve `${L2}` / `${L1}` display names through this file and hard-exit pointing
  // at it when a code is absent. A pack whose files are all present but whose code is unregistered
  // fails at the very first step, with an error that reads as a pipeline bug rather than a missing
  // line of YAML.
  const registry = byPath.get("language_codes.yml");
  if (registry === undefined) {
    findings.push({
      severity: "blocker",
      path: "language_codes.yml",
      message: "The language-code registry is missing.",
      fix: "Include `language_codes.yml` in the upload, or upload into a studio that already holds it.",
    });
  } else {
    const entries = parseLanguageRegistry(registry);
    for (const code of [input.l2, input.l1].filter((code) => code !== undefined)) {
      if (!registryHasCode(registry, code)) {
        findings.push({
          severity: "blocker",
          path: "language_codes.yml",
          message: `The registry has no entry for \`${code}\`.`,
          fix: `Add a line \`${code}: { name: "<display name>", iso639_1: "<xx>", iso639_3: "<xxx>" }\` — setting the language up in studio writes it for you.`,
        });
        continue;
      }
      // ADR-0120 Decision 4: codexflow reads the ISO codes from here. A warning, not a blocker,
      // while studio's own registry still predates the fields — l2_unit_plan needs only the name.
      const entry = entries.find((candidate) => candidate.code === code);
      if (entry?.iso639_1 === undefined || entry.iso639_3 === undefined) {
        findings.push({
          severity: "warning",
          path: "language_codes.yml",
          message: `The registry entry for \`${code}\` has no ISO 639-1 / 639-3 codes, so codexflow cannot run it.`,
          fix: `Edit its line to \`${code}: { name: "${entry?.name ?? "<display name>"}", iso639_1: "<xx>", iso639_3: "<xxx>" }\`.`,
        });
      }
    }
  }

  // The L2 side, when there is one. Returns the L2 scene-cast registry, which the L1 side needs for
  // its one cross-pack rule.
  const judgeL2Pack = (l2Dir: string): PersonasFile | undefined => {
    // ── The L2 pack ─────────────────────────────────────────────────────────────
    requireFile(
      `${l2Dir}/language_profile.md`,
      "The pack manifest `language_profile.md`",
      "Model it on `L2_QMS/language_profile.md`: display name and code, the register system's actual forms, the A1/A2/B scene model, and a `## Grammar inventory notes` section.",
    );

    const personas = requireFile(
      `${l2Dir}/personas.md`,
      "`personas.md`",
      "Model it on `L2_QMS/personas.md`: `# Recurring user personas`, one `## <FirstName>` block per persona, then `## Learner positioning` and `## Cast distribution rules`.",
    );
    if (personas !== undefined) {
      for (const section of REQUIRED_PERSONA_SECTIONS) {
        if (!personas.includes(section)) {
          findings.push({
            severity: "blocker",
            path: `${l2Dir}/personas.md`,
            message: `The \`${section}\` section is missing.`,
            fix: "Add it with that heading text exactly — `dialog_polish` excludes these two headings from persona parsing BY NAME, so a renamed one is parsed as a persona and fails the cast gate.",
          });
        }
      }
      const cast = castNames(personas);
      if (cast.length === 0) {
        findings.push({
          severity: "blocker",
          path: `${l2Dir}/personas.md`,
          message: "No persona sections found.",
          fix: "Give each persona its own `## <FirstName>` heading — the launch-time cast gate fails an empty cast file outright.",
        });
      } else {
        if (cast.length < 4) {
          findings.push({
            severity: "warning",
            path: `${l2Dir}/personas.md`,
            message: `Only ${cast.length} persona${cast.length === 1 ? "" : "s"} (${cast.join(", ")}); the convention is 4 main characters.`,
            fix: "Add personas until the cast covers mentor, peer-friend, community connector and teacher — a thin cast makes every unit sound like the same speaker.",
          });
        }
        for (const block of blocksOf(personas)) {
          if (isReservedSection(`## ${block.name}`)) continue;
          const absent = PERSONA_FIELDS.filter((field) => !block.body.includes(field));
          if (absent.length > 0) {
            findings.push({
              severity: "warning",
              path: `${l2Dir}/personas.md`,
              message: `Persona \`${block.name}\` is missing ${absent.join(", ")}.`,
              fix: "Fill every field to the depth of the `L2_QMS` cast — named neighbourhoods, concrete dialect features, real lexical items. A structurally valid but generically written persona produces flat dialogue.",
            });
          }
        }
      }
    }

    // ── The persona registry (ADR-0118) ─────────────────────────────────────────
    const registry = readRegistry(`${l2Dir}/${PERSONAS_FILE}`, "scene cast");
    if (registry !== undefined) {
      judgeRegistry(l2Dir, registry, ["main-character", "extra"]);
      if (personas !== undefined) {
        gateNames(
          castNames(personas),
          `${l2Dir}/personas.md`,
          registry,
          "main-character",
          `${l2Dir}/${PERSONAS_FILE}`,
        );
      }
    }

    requireFile(
      `${l2Dir}/reference_dialogues.md`,
      "`reference_dialogues.md`",
      "Provide the dialogue exemplars `dialog_polish` writes against; model them on `L2_QMS/reference_dialogues.md`.",
    );
    return registry;
  };
  const l2Registry = l2Dir === undefined ? undefined : judgeL2Pack(l2Dir);

  // ── The L1 pack ─────────────────────────────────────────────────────────────
  // Skipped entirely when no L1 is named: an upload that only defines an L2 is a legitimate thing to
  // do (the L1 side is shared across every L2 that audience learns, and usually exists already). The
  // New Curriculum modal judges each L1 on its own (`checkL1Pack`, via `languageReadiness`).
  if (l1Dir !== undefined) {
    const l1Profile = requireFile(
      `${l1Dir}/profile.md`,
      "The L1 profile",
      "Model it on `L1_QAE/profile.md`: the gloss conventions for this L1, plus the machine-read pronoun-noise marker.",
    );
    if (l1Profile !== undefined && !PRONOUN_NOISE_MARKER.test(l1Profile)) {
      findings.push({
        severity: "warning",
        path: `${l1Dir}/profile.md`,
        message: "The `pronoun-noise` marker is missing.",
        fix: "Add `<!-- pronoun-noise: i, you, he, she, we, they, it -->` with THIS L1's subject pronouns, lowercase. Without it the match-exercise parser silently falls back to the English set.",
      });
    }

    const tutors = requireFile(
      `${l1Dir}/tutors.md`,
      "`tutors.md`",
      "Model it on `L1_QAE/tutors.md`: one `## <FirstName>` block per tutoring voice, each declaring `**Voice / tone:**`.",
    );
    if (tutors !== undefined) {
      const voices = blocksOf(tutors).filter((block) => block.body.includes(TUTOR_VOICE_MARKER));
      if (voices.length === 0) {
        findings.push({
          severity: "blocker",
          path: `${l1Dir}/tutors.md`,
          message: "No tutor sections found.",
          fix: "Give each tutor a `## <FirstName>` heading and a `**Voice / tone:**` line — `validate_tutors_md` keys on that marker and exits when it finds none.",
        });
      }
    }

    const l1Registry = readRegistry(`${l1Dir}/${PERSONAS_FILE}`, "tutor");
    if (l1Registry !== undefined) {
      judgeRegistry(l1Dir, l1Registry, ["tutor"]);
      if (tutors !== undefined) {
        gateNames(
          blocksOf(tutors)
            .filter((block) => block.body.includes(TUTOR_VOICE_MARKER))
            .map((block) => block.name),
          `${l1Dir}/tutors.md`,
          l1Registry,
          "tutor",
          `${l1Dir}/${PERSONAS_FILE}`,
        );
      }
      if (l2Registry !== undefined) {
        const l2Ids = new Set(l2Registry.personas.map((persona) => persona.id));
        for (const persona of l1Registry.personas.filter((p) => l2Ids.has(p.id))) {
          findings.push({
            severity: "blocker",
            path: `${l1Dir}/${PERSONAS_FILE}`,
            message: `Persona id \`${persona.id}\` is defined in both \`${l2Dir}\` and \`${l1Dir}\`.`,
            fix: "A run merges the two registries, so an id may live in only one of them: scene characters in the L2 pack, tutors in the L1 pack.",
          });
        }
      }
    }
  }

  const l2 = input.l2;
  if (l2 !== undefined && l2Dir !== undefined) {
    // ── The topic files (ADR-0120) and the check manifest (ADR-0123) ─────────────
    // `personas.yml` is judged by `readRegistry` above, in more detail, so its topic blocker would only
    // repeat it.
    const topics = readPackTopics(l2, byPath);
    for (const blocker of topics.blockers) {
      if (blocker._tag === "Invalid" && blocker.topic === "personas") continue;
      findings.push(topicFinding(l2, blocker));
    }

    // A check switched off while the pack holds everything it needs. Not a blocker — off is a decision —
    // but a disabled check never runs, so when the data arrives after the manifest (a conversion that
    // switched it off for lack of data, then an upload of the lists) it stays off without a word.
    for (const id of CATALOG_CHECK_IDS) {
      if (topics.data.checks?.checks[id]?.enabled !== false) continue;
      if (!CHECK_CATALOG[id].requires.every((ref) => hasPackKey(topics.data, ref))) continue;
      findings.push({
        severity: "warning",
        path: topicFilePath(l2, "checks"),
        message: `\`${id}\` is switched off, but the pack holds all the data it needs.`,
        fix: `Set \`${id}: { enabled: true }\` in \`checks.yml\` ${EDITOR}, with \`samples\` it accepts (\`pass\`) and flags (\`fail\`), if the check should run for this language; while it is off, it is skipped.`,
      });
    }

    // Data in a place no runtime reads any more. Not a blocker — nothing reads it, so nothing fails on
    // it — but the check it fed now needs the topic key instead, and a human must move it there.
    for (const { file, ref } of LEGACY_PACK_FILES) {
      const path = `${l2Dir}/${file}`;
      if (!byPath.has(path)) continue;
      findings.push({
        severity: "warning",
        path,
        message: `\`${file}\` is no longer read.`,
        fix: `Move its entries into ${keyInFile(ref)}, then delete this file.`,
      });
    }
    const legacyProfile = byPath.get(`${l2Dir}/language_profile.md`);
    for (const { marker, ref } of LEGACY_PROFILE_MARKERS) {
      if (legacyProfile === undefined || legacyMarkerValue(legacyProfile, marker) === undefined)
        continue;
      findings.push({
        severity: "warning",
        path: `${l2Dir}/language_profile.md`,
        message: `The \`<!-- ${marker}: … -->\` marker is no longer read.`,
        fix: `Move its entries into ${keyInFile(ref)}, then delete the marker.`,
      });
    }
  }

  // The template ships every file pre-filled with `<<FILL: …>>` markers. Uploading one with markers
  // still in it is the likeliest way to get a structurally valid, semantically empty pack — every
  // heading present, nothing said — so each such file is named rather than passing quietly. Only the
  // packs being judged, plus the shared registry: another language's placeholders are its own report's.
  const judged = [l2Dir, l1Dir].filter((dir) => dir !== undefined);
  const isJudged = (path: string) =>
    path === "language_codes.yml" || judged.some((dir) => path.startsWith(`${dir}/`));
  for (const file of input.files.filter((file) => isJudged(file.path))) {
    if (file.content.includes(FILL_MARKER)) {
      const count = file.content.split(FILL_MARKER).length - 1;
      findings.push({
        severity: options.placeholdersBlock ? "blocker" : "warning",
        path: file.path,
        message: `Still has ${count} unfilled \`${FILL_MARKER}…>>\` placeholder${count === 1 ? "" : "s"} from the template.`,
        fix: "Replace each one with real content, to the depth of the `L2_QMS` pack — concrete dialect features, named places, real lexical items. A pack whose placeholders survive generates flat, generic material.",
      });
    }
  }

  if (l2 !== undefined && l2Dir !== undefined) {
    const profile = byPath.get(`${l2Dir}/language_profile.md`);
    if (profile !== undefined && !profile.includes("## Grammar inventory notes")) {
      findings.push({
        severity: "warning",
        path: `${l2Dir}/language_profile.md`,
        message: "No `## Grammar inventory notes` section.",
        fix: "Steps 0s/0a read it (verb allowlist, deferrals, example register) when generating this L2's grammar inventory. With no upstream inventory record for this L2, the generated arm is the only route and this section is its only input.",
      });
    }
    if (!byPath.has(`${l2Dir}/${VOCAB_INVENTORY}`)) {
      findings.push({
        severity: "warning",
        path: `${l2Dir}/${VOCAB_INVENTORY}`,
        message: "No vocab reference inventory.",
        fix: "Step 1 inlines it unconditionally, so a run without it fails with `FileNotFoundError` once the cluster is up. Provide an `items:` list of rows with `item_<L2>`, `item_<L1>`, `part of speech`, `CEFR` and `gender` — the template's file shows the shape.",
      });
    }

    findings.push({
      severity: "info",
      message: "The grammar ledger is generated, not uploaded.",
      fix: `Run \`run_pipeline.py --l2 ${l2} --l1 ${input.l1 ?? "<L1>"} --cefr A1.1 --step 0\` once per pair × CEFR before the first full run; if \`curriculum_grammars.json\` has no record for this L2, add \`--grammar-source generated\`.`,
    });
  }

  return { l2: input.l2, l1: input.l1, findings, complete: !hasBlocker(findings) };
};

/** Every language code present in a set of pack paths, split by side. */
export const languageCodesOfPaths = (
  paths: readonly string[],
): { readonly l1: readonly string[]; readonly l2: readonly string[] } => {
  const l1 = new Set<string>();
  const l2 = new Set<string>();
  for (const path of paths) {
    const [root] = path.split("/");
    if (root === undefined) continue;
    if (root.startsWith("L1_")) l1.add(root.slice(3));
    if (root.startsWith("L2_")) l2.add(root.slice(3));
  }
  return { l1: [...l1].sort(), l2: [...l2].sort() };
};

/**
 * Normalise a path as a browser hands it over, into a pack-relative path.
 *
 * A directory picker yields `webkitRelativePath` rooted at whatever the human selected: the `packs/`
 * directory, one `L2_FRA/` directory, or a folder with an arbitrary name. All three must land on the
 * same pack-relative path, so this drops every leading segment before the first `L1_`/`L2_` root —
 * or, for the registry, keeps only its filename. Returns `undefined` for a path with no
 * pack-relative interpretation (a stray `.DS_Store`, an image, a README).
 */
export const packRelativePath = (rawPath: string): string | undefined => {
  const segments = rawPath.split("/").filter((segment) => segment.length > 0);
  // The WHOLE segment must be a language root, the shape `LanguagePackPath` admits. A prefix match
  // would anchor on a wrapper folder such as `L2_FRA-pack/` — the name the template archive used to
  // unpack to — and every file under it would then be refused as not a pack document.
  const rootIndex = segments.findIndex((segment) => /^L[12]_[A-Za-z]+$/.test(segment));
  if (rootIndex >= 0) {
    const relative = segments.slice(rootIndex).join("/");
    return relative.endsWith(".md") || relative.endsWith(".yml") ? relative : undefined;
  }
  return segments.at(-1) === "language_codes.yml" ? "language_codes.yml" : undefined;
};

/** One `language_codes.yml` line. The ISO fields are absent on lines written before ADR-0120. */
export interface LanguageRegistryEntry {
  readonly code: string;
  readonly name: string;
  readonly iso639_1?: string;
  readonly iso639_3?: string;
  // What prompts call the language when its display name is not what the model should be told (QMS is
  // shown as "Latin American Spanish" but taught as "Mexican Spanish"). Absent means the name.
  readonly variety?: string;
  // CON-2158: the Slack channel a push of this language is announced in. Absent means the server
  // tries `project-curriculum-<l1 iso>-<l2 iso>`. Edited on studio's Language packs page.
  readonly slack_channel?: string;
  // ADR-0134: the learner locale (BCP 47, e.g. `en-US`) whose exercise-instruction table a
  // curriculum with this L1 shows. Absent means the L1 has no instructions, so it does not convert.
  readonly locale?: string;
}

const REGISTRY_LINE = /^\s*([A-Za-z0-9_]+)\s*:\s*\{(.*)\}\s*$/;
const REGISTRY_FIELD = /([A-Za-z0-9_]+)\s*:\s*["']?([^"',}]+?)["']?\s*(?:,|$)/g;

/**
 * The entries of `language_codes.yml`.
 *
 * Parsed with a line regex rather than a YAML library: the file's own header documents exactly one
 * admissible shape (`<CODE>: { name: "<display name>", iso639_1: "<xx>", iso639_3: "<xxx>" }`),
 * studio only ever writes that shape, and pulling a YAML parser into `core` for four lines of flow
 * mapping would be the larger liability. A line that does not match, or has no `name`, is skipped,
 * so comments and blanks cost nothing.
 */
export const parseLanguageRegistry = (content: string): readonly LanguageRegistryEntry[] => {
  const entries: LanguageRegistryEntry[] = [];
  for (const line of content.split("\n")) {
    const match = REGISTRY_LINE.exec(line);
    if (match?.[1] === undefined || match[2] === undefined) continue;
    const fields = new Map(
      [...match[2].matchAll(REGISTRY_FIELD)].map((field) => [field[1], field[2]?.trim()] as const),
    );
    const name = fields.get("name");
    if (name === undefined) continue;
    const iso639_1 = fields.get("iso639_1");
    const iso639_3 = fields.get("iso639_3");
    const slackChannel = fields.get("slack_channel");
    const variety = fields.get("variety");
    const locale = fields.get("locale");
    entries.push({
      code: match[1],
      name,
      ...(variety === undefined ? {} : { variety }),
      ...(iso639_1 === undefined ? {} : { iso639_1 }),
      ...(iso639_3 === undefined ? {} : { iso639_3 }),
      ...(slackChannel === undefined ? {} : { slack_channel: slackChannel }),
      ...(locale === undefined ? {} : { locale }),
    });
  }
  return entries;
};

/** One language studio knows about, and whether a run may be launched against it. */
export const LanguageReadiness = Schema.Struct({
  code: Schema.String,
  // From the registry when it has an entry, else the code itself — a pack whose files landed but
  // whose registry line did not is exactly the state this surface has to be able to show.
  name: Schema.String,
  // A code may be used as either side, and several are (`QAE` is an L2 pack and the L1 of every
  // pair). Both flags rather than one enum, so the New Curriculum modal can filter each select on
  // the side it is asking about.
  isL1: Schema.Boolean,
  isL2: Schema.Boolean,
  // L2 completeness only — the L2 side of `checkLanguagePack`, with no L1 named. The L1 side is
  // `l1Complete` below.
  complete: Schema.Boolean,
  blockers: Schema.Array(Schema.String),
  // L1 completeness — `checkL1Pack` over this code's `L1_` pack, judged only when it has one (false
  // and empty otherwise). The New Curriculum modal offers an L1 only once it is complete: a run hard-
  // exits on a missing or empty `profile.md`/`tutors.md`, or a tutor its registry does not hold.
  l1Complete: Schema.Boolean,
  l1Blockers: Schema.Array(Schema.String),
  // The CEFR levels this L2's inventories can carry — see `inventoryLevels`. Absent when the
  // inventory is not something studio can read, which never locks a level.
  grammarLevels: Schema.optional(Schema.Array(Schema.String)),
  vocabLevels: Schema.optional(Schema.Array(Schema.String)),
});
export type LanguageReadiness = typeof LanguageReadiness.Type;

// ── Per-level inventory coverage ────────────────────────────────────────────────
// Steps 0i and 0v slice the pack's reference inventories by the run's CEFR level and hard-fail on
// an empty slice, minutes into a run. Studio holds both files, so it can refuse those levels up
// front instead. The rules mirror the pipeline's own slicers:
//
//   * grammar (`grammar-import.ts::extractSublevel`) is keyed per SUBLEVEL (`A1.1:`). A sublevel
//     needs its own block; a band (`A1`) needs a block for EVERY member (`A1.1` and `A1.2`).
//   * vocab (`step0.ts::runStep0v`) filters items on their `CEFR:` field by BAND, so `A1.1`, `A1.2`
//     and `A1` all need items tagged `A1`.
//
// The vocab inventory may instead ship as `full_vocab_inventory.reference.yml.zip` (QMS does), which
// the store cannot hold — so a missing vocab file means "unknown", not "empty". A missing grammar
// file has no such alternative and does mean empty.

export const GRAMMAR_INVENTORY_FILE = "grammar/full_grammar_inventory.reference.yml";
export const VOCAB_INVENTORY_FILE = "vocab/full_vocab_inventory.reference.yml";

const GRAMMAR_BLOCK_KEY = /^([ABC][12]\.[12]):\s*$/;
const VOCAB_CEFR_FIELD = /^\s*CEFR:\s*["']?([ABC][12])\b/gm;

/** The sublevels with at least one entry in a full grammar inventory. */
const grammarLevelsOf = (content: string): readonly string[] => {
  const levels: string[] = [];
  let current: string | undefined;
  for (const line of content.split("\n")) {
    const key = GRAMMAR_BLOCK_KEY.exec(line)?.[1];
    if (key !== undefined) {
      current = key;
    } else if (current !== undefined && /^\s*- /.test(line) && !levels.includes(current)) {
      levels.push(current);
    }
  }
  return levels;
};

/** The bands with at least one item in a vocab inventory. */
const vocabLevelsOf = (content: string): readonly string[] => [
  ...new Set(Array.from(content.matchAll(VOCAB_CEFR_FIELD), (match) => match[1] ?? "")),
];

/** Which CEFR levels an L2 pack's grammar and vocab inventories cover. */
export const inventoryLevels = (
  l2: string,
  files: readonly PackFile[],
): { readonly grammarLevels: readonly string[]; readonly vocabLevels?: readonly string[] } => {
  const grammar = files.find((file) => file.path === `L2_${l2}/${GRAMMAR_INVENTORY_FILE}`);
  const vocab = files.find((file) => file.path === `L2_${l2}/${VOCAB_INVENTORY_FILE}`);
  return {
    grammarLevels: grammar === undefined ? [] : grammarLevelsOf(grammar.content),
    ...(vocab === undefined ? {} : { vocabLevels: vocabLevelsOf(vocab.content) }),
  };
};

export type MissingInventory = "grammar" | "vocab";

/** The inventories that cannot carry a run at `level` — empty when both can, or are unknown. */
export const missingInventories = (
  readiness: Pick<LanguageReadiness, "grammarLevels" | "vocabLevels">,
  level: string,
): readonly MissingInventory[] => {
  const band = level.split(".")[0] ?? level;
  const needed = level.includes(".") ? [level] : [`${band}.1`, `${band}.2`];
  const missing: MissingInventory[] = [];
  const { grammarLevels, vocabLevels } = readiness;
  if (grammarLevels !== undefined && !needed.every((sub) => grammarLevels.includes(sub))) {
    missing.push("grammar");
  }
  if (vocabLevels !== undefined && !vocabLevels.includes(band)) missing.push("vocab");
  return missing;
};

/** What to tell a human about `missingInventories`: which file, and what it needs. */
export const missingInventoryMessage = (
  l2: string,
  level: string,
  missing: readonly MissingInventory[],
): string => {
  const band = level.split(".")[0] ?? level;
  const needs = missing.map((kind) =>
    kind === "grammar"
      ? `L2_${l2}/${GRAMMAR_INVENTORY_FILE} needs ${level.includes(".") ? `an ${level}` : `both ${band}.1 and ${band}.2`} block`
      : `L2_${l2}/${VOCAB_INVENTORY_FILE} needs items with CEFR: ${band}`,
  );
  return `${level} can't be generated for ${l2} yet: ${needs.join("; ")}. Fill it in the language pack first.`;
};

/**
 * Fold every pack file studio holds into the per-language answer the "Language taught" select
 * needs: which codes exist, what to call them, and whether each one's L2 pack can actually carry a
 * run.
 *
 * A code appears here if the registry names it OR a pack directory exists for it — deliberately the
 * union, because both halves of that state are wrong in an interesting way. A registry line with no
 * files is a language someone declared and never uploaded; files with no registry line is a pack
 * that will hard-exit at launch. Hiding either would hide the reason the option is unavailable.
 */
export const languageReadiness = (files: readonly PackFile[]): readonly LanguageReadiness[] => {
  const registry = files.find((file) => file.path === "language_codes.yml");
  const names = new Map(
    (registry === undefined ? [] : parseLanguageRegistry(registry.content)).map((entry) => [
      entry.code,
      entry.name,
    ]),
  );
  const { l1, l2 } = languageCodesOfPaths(files.map((file) => file.path));
  const codes = [...new Set([...names.keys(), ...l1, ...l2])].sort();
  return codes.map((code) => {
    const isL2 = l2.includes(code);
    // Only a code that HAS an L2 pack is judged as one. Running the L2 check over an L1-only code
    // (`QAE` is the L1 of every pair and has no `L2_QAE` tree in some deployments) would report it
    // as incomplete for missing L2 files it is not supposed to have — nonsense in the modal, and
    // the kind of nonsense a human learns to ignore.
    const blockers = isL2
      ? checkLanguagePack({ l2: code, files })
          .findings.filter((finding) => finding.severity === "blocker")
          .map((finding) => finding.message)
      : [];
    const isL1 = l1.includes(code);
    const l1Blockers = isL1
      ? checkL1Pack({ l1: code, files })
          .findings.filter((finding) => finding.severity === "blocker")
          .map((finding) => finding.message)
      : [];
    return {
      code,
      name: names.get(code) ?? code,
      isL1,
      isL2,
      complete: isL2 && blockers.length === 0,
      blockers,
      l1Complete: isL1 && l1Blockers.length === 0,
      l1Blockers,
      ...(isL2 ? inventoryLevels(code, files) : {}),
    };
  });
};

/**
 * Add or rename one registry line, keeping its other fields and everything else in the file. `fields`
 * fills in the line's other fields where it has none — a new line's, for one.
 */
export const withRegistryEntry = (
  registry: string,
  code: string,
  name: string,
  fields: Omit<LanguageRegistryEntry, "code" | "name"> = {},
): string => {
  const lines = registry.split("\n");
  const index = lines.findIndex((line) => line.trim().startsWith(`${code}:`));
  const existing = {
    ...fields,
    ...(index >= 0 ? parseLanguageRegistry(lines[index] ?? "")[0] : undefined),
  };
  const iso = [
    existing.iso639_1 === undefined ? "" : `, iso639_1: "${existing.iso639_1}"`,
    existing.iso639_3 === undefined ? "" : `, iso639_3: "${existing.iso639_3}"`,
    existing.variety === undefined ? "" : `, variety: "${existing.variety}"`,
    existing.slack_channel === undefined ? "" : `, slack_channel: "${existing.slack_channel}"`,
    existing.locale === undefined ? "" : `, locale: "${existing.locale}"`,
  ].join("");
  const entry = `${code}: { name: "${name}"${iso} }`;
  if (index >= 0) {
    lines[index] = entry;
    return lines.join("\n");
  }
  const trimmed = registry.replace(/\n+$/, "");
  return `${trimmed}\n${entry}\n`;
};

// The largest pack document studio accepts in one write (adding a pack file): a pack is a
// couple of dozen small files, so the bound sits far above any honest document and exists only so a
// JSON body cannot exhaust memory — the same number as `MAX_IMPORT_BYTES`.
export const MAX_PACK_FILE_BYTES = 1_048_576;

// ── Known languages ─────────────────────────────────────────────────────────────
// Every Babbel language code studio knows a display name for. The template's `language_codes.yml`
// is generated from this, so a new pack arrives with its own registry line already written, and the
// upload form offers these codes rather than asking a human to remember them.
//
// The three codes the registry already held keep their stored names verbatim (`QMS`, `QAE`, `ITA`)
// — an upload must never rename a language as a side effect. The rest are Babbel's product
// languages. A registry line only names a language; a code with no pack files is listed as declared
// but not ready, never offered for generation.
export const KNOWN_LANGUAGES: readonly (Required<
  Omit<LanguageRegistryEntry, "slack_channel" | "variety" | "locale">
> & {
  readonly variety?: string;
  readonly slack_channel?: string;
})[] = [
  { code: "QAE", name: "American English", iso639_1: "en", iso639_3: "eng" },
  {
    code: "QMS",
    name: "Latin American Spanish",
    variety: "Mexican Spanish",
    iso639_1: "es",
    iso639_3: "spa",
  },
  { code: "ITA", name: "Standard Italian", iso639_1: "it", iso639_3: "ita" },
  { code: "DAN", name: "Danish", iso639_1: "da", iso639_3: "dan" },
  { code: "DEU", name: "German", iso639_1: "de", iso639_3: "deu" },
  { code: "ENG", name: "English", iso639_1: "en", iso639_3: "eng" },
  { code: "FRA", name: "French", iso639_1: "fr", iso639_3: "fra" },
  { code: "IND", name: "Indonesian", iso639_1: "id", iso639_3: "ind" },
  { code: "NLD", name: "Dutch", iso639_1: "nl", iso639_3: "nld" },
  { code: "NOR", name: "Norwegian", iso639_1: "no", iso639_3: "nor" },
  { code: "POL", name: "Polish", iso639_1: "pl", iso639_3: "pol" },
  { code: "POR", name: "Portuguese", iso639_1: "pt", iso639_3: "por" },
  { code: "RUS", name: "Russian", iso639_1: "ru", iso639_3: "rus" },
  {
    code: "SPA",
    name: "Spanish", // l2-literal: allow (one entry, split by the formatter)
    iso639_1: "es",
    iso639_3: "spa",
    slack_channel: "project-curriculum-en-es",
  },
  { code: "SWE", name: "Swedish", iso639_1: "sv", iso639_3: "swe" },
  { code: "TUR", name: "Turkish", iso639_1: "tr", iso639_3: "tur" },
];

/** The display name studio knows for a code, if any. */
export const knownLanguageName = (code: string): string | undefined =>
  KNOWN_LANGUAGES.find((language) => language.code === code)?.name;

// ── The template ────────────────────────────────────────────────────────────────
// A pack skeleton that mirrors the real packs file for file and section for section (`L2_ITA`,
// `L1_QAE`), generated from the very constants the rules above check, so the two cannot drift. A
// freshly downloaded template is blocked ONLY by the decisions a human must make — each check in
// `checks.yml`, which its schema refuses as a placeholder, and each persona's name and voice, which
// fail at push as placeholders (ADR-0140) — and every other gap is a `<<FILL: …>>` warning naming
// its file. `rubric.yml` is not in it: the file is optional (with no rules a run reads
// the shared rubric alone, CON-1624), and its schema refuses a file without a rule.
//
// Everything that is STRUCTURE is written out in full: headings the prompts reference by name, the
// bold field markers the validators key on, the HTML marker the L1 parser reads, each YAML file's
// exact shape, and the cast identities, which are not linguistic content. Everything that is
// KNOWLEDGE of the language is a placeholder (ADR-0021): what a persona sounds like and looks like,
// which register forms exist, which words are function words.

const fill = (what: string): string => `${FILL_MARKER} ${what}>>`;

// The cast slots every new pack starts from (ADR-0118). Names, prose and voices are the pack's own,
// so each slot is a placeholder; only what a cast needs structurally is written out — four main
// characters covering the four archetypes, a mix of gender and age, one default narrator. The ids
// are placeholders too: an id is the persona's lowercased first name (ADR-0127), set before the
// first upload.
const TEMPLATE_CAST = [
  {
    id: "main_1",
    name: fill("first name of main character 1"),
    gender: "male",
    age: "middle-aged",
    role: "workplace contact / older mentor figure",
    register: "formal",
    defaultNarrator: true,
  },
  {
    id: "main_2",
    name: fill("first name of main character 2"),
    gender: "male",
    age: "young",
    role: "peer / friend",
    register: "informal",
    defaultNarrator: false,
  },
  {
    id: "main_3",
    name: fill("first name of main character 3"),
    gender: "female",
    age: "young",
    role: "peer / community connector",
    register: "informal",
    defaultNarrator: false,
  },
  {
    id: "main_4",
    name: fill("first name of main character 4"),
    gender: "female",
    age: "middle-aged",
    role: "teacher / mentor",
    register: "formal-in-class",
    defaultNarrator: false,
  },
] as const;
const TEMPLATE_TUTORS = [
  {
    id: "tutor_1",
    name: fill("first name of tutor 1"),
    gender: "female",
    age: "middle-aged",
    defaultNarrator: false,
  },
  {
    id: "tutor_2",
    name: fill("first name of tutor 2"),
    gender: "male",
    age: "middle-aged",
    defaultNarrator: false,
  },
] as const;

// `personas.yml` for a template: identities written out, everything a human must decide a
// placeholder. Rendered through `renderPersonasFile`, so the file parses by construction and the
// upload's registry check has nothing to block on — only `<<FILL` warnings remain.
const personaRegistry = (
  entries: readonly {
    readonly id: string;
    readonly name: string;
    readonly gender: "male" | "female";
    readonly age: "young" | "middle-aged";
    readonly defaultNarrator: boolean;
  }[],
  role: "main-character" | "tutor",
): string =>
  renderPersonasFile({
    lighting: fill(
      "the lighting spec every persona image is generated under, e.g. soft, even, high-key daylight on a plain white background",
    ),
    personas: entries.map((entry) => ({
      id: PersonaId.make(entry.id),
      display_name: entry.name,
      role,
      gender: entry.gender,
      age: entry.age,
      description: fill(
        "appearance prose for the image model: build and posture, face and hair, clothing and accessories",
      ),
      voice: {
        provider: "elevenlabs",
        voice_id: fill(`ElevenLabs voice id for ${entry.name}, speaking this language`),
        speed: 1,
      },
      deprecated: false,
      default_narrator: entry.defaultNarrator,
    })),
  });

// The L1 every existing pair is taught to, and so the gloss column a vocab inventory needs when no
// L1 is named.
const DEFAULT_L1 = "QAE";

const displayName = (code: string): string =>
  knownLanguageName(code) ?? fill(`display name of ${code}`);

const LANGUAGE_CODES = (codes: readonly string[]): string => {
  const extra = codes.filter((code) => knownLanguageName(code) === undefined);
  return [
    "# Language-code registry — maps the Babbel-internal codes passed via the",
    "# pipeline's --l1 / --l2 parameters to a human display name (the L1 / L2",
    "# prompt variables). Codes are NOT a public standard. A code may be used",
    "# as either L1 or L2.",
    "#",
    "# On upload, only codes studio does not already have are added; an existing",
    "# language's name is never changed from here (use the form's Display name).",
    "#",
    '#   <CODE>: { name: "<display name>", iso639_1: "<xx>", iso639_3: "<xxx>" }',
    "#",
    '# Add `, variety: "<name>"` to a line when prompts should call the language something other',
    "# than its display name (e.g. a regional variety); without it, prompts use the name.",
    "#",
    '# Add `, slack_channel: "<channel>"` to a line to announce that language\'s',
    "# pushes to CSS there; without it, studio uses project-curriculum-<l1>-<l2>, unless another",
    "# language shares the ISO 639-1 code.",
    "#",
    '# Add `, locale: "<xx-YY>"` to a line to say which exercise-instruction table learners with',
    "# that L1 read; without it, studio uses the one table in that ISO 639-1 language, if exactly",
    "# one exists.",
    "",
    ...KNOWN_LANGUAGES.map(
      (language) =>
        `${language.code}: { name: "${language.name}", iso639_1: "${language.iso639_1}", iso639_3: "${language.iso639_3}"${language.variety === undefined ? "" : `, variety: "${language.variety}"`}${language.slack_channel === undefined ? "" : `, slack_channel: "${language.slack_channel}"`} }`,
    ),
    ...extra.map(
      (code) =>
        `${code}: { name: "${displayName(code)}", iso639_1: "${fill(`ISO 639-1 code of ${code}`)}", iso639_3: "${fill(`ISO 639-3 code of ${code}`)}" }`,
    ),
    "",
  ].join("\n");
};

const marker = (key: string, what: string): string => `<!-- ${key}: ${fill(what)} -->`;

const LANGUAGE_PROFILE = (l2: string, l1: string): string =>
  [
    `# Language profile — L2_${l2} (${displayName(l2)})`,
    "",
    `Pack manifest for the L2 = **${displayName(l2)}** (\`${l2}\`) curriculum. This file`,
    "registers the L2-specific conventions the pipeline relies on; the cast, scene",
    "model, and culture content live in the sibling files named below. It is inlined",
    "WHOLE into the grammar-inventory and exercise-generation prompts, so write it for",
    "a reader who knows nothing about this language. `L2_ITA` and `L2_QMS` in studio",
    "show the depth expected.",
    "",
    `- **Display name:** ${displayName(l2)} (from \`language_codes.yml\`).`,
    `- **Register system:** ${fill('the ACTUAL address forms and when each is used — e.g. "informal tu vs. formal Lei" — never "the appropriate formal register"')}`,
    "  Per-character register and the unit-level register-contrast rules are defined in",
    "  `personas.md` (`Register with learner`, `Cast distribution rules`).",
    "- **Scenario / scene model:** the `A1` (in-culture everyday) / `A2` (in-culture",
    `  transactional) / \`B\` (local encounter, ${fill("the diaspora community in the learner's country")})`,
    "  archetypes, the per-persona `Default scene contexts`, and the learner-positioning",
    "  rules all live in `personas.md`.",
    "- **Culture & pragmatics:** AHA gate + module shape in the language-agnostic",
    "  `inputs/authoring_guides/CP_Module_Authoring_Guide.md`.",
    `- **Grammar:** ${fill("whether an upstream grammar-inventory record exists for this language; if not, say to use the generated grammar-source arm (steps 0s + 0a), driven by the Grammar inventory notes below")}`,
    "",
    "## Pack files",
    "",
    "| File | Role |",
    "|------|------|",
    "| `personas.md` | Recurring cast, register, scene archetypes, learner positioning |",
    "| `reference_dialogues.md` | Approved dialogue exemplars (style anchors) |",
    "| `vocab/full_vocab_inventory.reference.yml` | CEFR vocab reference (step 1) |",
    "| `personas.yml` | Machine-read cast: ids, gender, voices, appearance (ADR-0118) |",
    "| `checks.yml` | Check manifest: every catalog check, enabled or not (ADR-0123) |",
    "| `orthography.yml` | How the language is written: decisive characters, letters, marks |",
    "| `lexicon.yml` | Word lists: function words, country names, speech verbs, demonyms |",
    "| `grammar.yml` | Grammar data: gender-pair exemptions, essential first verbs |",
    "| `register.yml` | Forms of address and politeness |",
    "| `dialect.yml` | The variety taught and the forms it avoids |",
    "| `prompt_examples.yml` | Examples the prompts read |",
    "| `rubric.yml` | This language's own rules, appended to the curriculum quality rubric |",
    "",
    "## Grammar inventory notes",
    "",
    "L2-specific addenda for grammar inventory generation (steps 0s / 0a) and review, per",
    "`inputs/authoring_guides/Grammar_Inventory_Authoring_Guide.md` (which is",
    "language-agnostic and defers to this section).",
    "",
    "### Verb-entry allowlist (guide §2.2)",
    "",
    "Only these lemmas may be named in an entry title as *the* teachable object, from the",
    "sublevel indicated. Every other verb appears under conjugation-pattern or",
    "irregular-subgroup titles.",
    "",
    "| Verb | Rationale | From |",
    "|---|---|---|",
    `| ${fill("verb")} | ${fill("why it earns its own entry (unique paradigm, foundational use)")} | A1.1 |`,
    `| ${fill("verb")} | ${fill("why it earns its own entry")} | A1.2 |`,
    "",
    "### A1.1 deferrals and limits (tighten guide §4)",
    "",
    `- ${fill("what is held back from A1.1 and to which level — periphrases, clitics, imperatives, preposition grids, clock/calendar limits, irregular plurals")}`,
    "",
    "### Example register policy (guide §2.4)",
    "",
    `- Variety: ${fill("the standard variety taught, and which regional forms are excluded")}`,
    `- Settings, names, and toponyms: ${fill("the country and diaspora the examples are set in")}, consistent with the persona world in \`personas.md\`.`,
    "- Examples are L2-only; no L1 words inside example strings (numbers and times written",
    "  in the L2).",
    "",
    "## Dialogue notes",
    "",
    "L2-specific addenda for dialogue authoring (the exercise-generation step), per",
    "`inputs/authoring_guides/Dialogue_Module_Authoring_Guide.md` (which is",
    "language-agnostic and defers to this section). Address/register and per-character",
    "dialect are NOT restated here — they live in the register system above and in",
    "`personas.md`.",
    "",
    "### Voice markers (A1.1 calibration)",
    "",
    "Cap at **1–2 per dialogue** (even allowed ones — cumulative density is the real risk).",
    "",
    `- **Allow-set (non-gap turns):** ${fill("high-frequency, transparent, pan-regional fillers")}`,
    `- **Deny-set at A1.1:** ${fill("region-narrow or opaque markers a beginner cannot decode")}`,
    `- **Figurative-idiom ban is absolute** at A1.1, even as passive context. ${fill("two or three idioms to avoid")}`,
    "",
    "### Common correctness traps (front-loaded from step-4 axis 7)",
    "",
    "Flat errors that mislead an A1 learner; the step-4 polish flags them but does not",
    "auto-fix gap-target errors — avoid producing them here.",
    "",
    `- ${fill("a trap: the wrong form, the right form, and why (agreement, verb choice, clock or number agreement, response licensing)")}`,
    "",
    "### Dictate homophone cautions (`dialogue_choose_dictate`)",
    "",
    fill(
      "which sound contrasts are hard to hear for this audience, so dictate distractors do not hinge on them",
    ),
    "",
    "### Numeric-formation boundaries",
    "",
    fill("number-formation quirks a distractor numeral must not cross before they are taught"),
    "",
    `### L1-interference watch-outs (current audience L1 = ${displayName(l1)})`,
    "",
    `- ${fill("false friends and calques from the audience L1: the L2 word, what it really means, and the word the learner actually wants")}`,
    "",
  ].join("\n");

// One cast slot of `personas.md`. `role` and `register` are the template's archetype for the slot;
// a cast taken from a dropped `personas.yml` has neither, and leaves both to the placeholder.
interface PersonaBlockSlot {
  readonly name: string;
  readonly role?: string;
  readonly register?: string;
}

const personaBlock = (persona: PersonaBlockSlot): string =>
  [
    "---",
    "",
    `## ${persona.name}`,
    "",
    `- **Age / origin:** ${fill("age range; home city and region")}`,
    `- **Role:** ${persona.role === undefined ? "" : `${persona.role} — `}${fill("occupation, and what they are like at it")}`,
    `- **Register with learner:** ${fill(`the actual ${persona.register ?? "formal or informal"} address form the learner uses, and when it changes`)}`,
    `- **Voice / quirks:** ${fill("temperament, favourite topics, a habit or two")}`,
    `- **Regional / dialect features:** ${fill("pronunciation, signature lexicon with glosses, politeness habits")}`,
    "- **Default scene contexts (A1 / A2 / B):**",
    `  - *A1 (in-culture everyday):* ${fill("three named everyday settings in their home city or region")}`,
    `  - *A2 (in-culture transactional):* ${fill("three named service settings: airport, pharmacy, ticket window, hotel")}`,
    `  - *B (local encounter, diaspora community):* ${fill("three named settings in the learner's country")}`,
    "",
  ].join("\n");

const PERSONAS = (l2: string, cast: readonly PersonaBlockSlot[] = TEMPLATE_CAST): string =>
  [
    "# Recurring user personas",
    "",
    "This file defines the curriculum's recurring cast; every unit plan draws its named",
    "characters from this set, with `(one-off cameo)` for one-time characters.",
    "",
    "- `Register with learner` states how the *learner* addresses this character",
    "  (asymmetric on purpose).",
    "- The recurring cast is fixed at **4 main characters** — do not invent additional",
    "  recurring characters. The tutoring voices that speak the L1 `tutor_*` explanations",
    "  are not scene characters; they live in the L1 pack (`L1_<l1>/tutors.md`).",
    "",
    "<!-- TEMPLATE: each persona heading must match a `display_name` of an active",
    "main-character in the sibling `personas.yml`, which carries the machine-read half of",
    "the cast (id, gender, voice, appearance). Write each bio for THIS language and culture.",
    "Rename a persona in both files at once. Set each id in `personas.yml` to the persona's",
    "first name, lowercased, before the first upload; never change an id once content uses it. -->",
    "",
    REQUIRED_PERSONA_SECTIONS[0],
    "",
    `The learner is a ${fill("where the learner lives and what their first language is")} studying ${displayName(l2)} to`,
    `(a) ${fill("interact with speakers they meet at home")} and (b) ${fill("travel to or work with people where it is spoken")}.`,
    "**The learner is never a named character in any scene narrative.** Integrated",
    "Dialogues, Communicative situations, Named settings, and L1 scene-setting paragraphs",
    'are third-person about the recurring personas — no "the learner", "you", or "we".',
    "Speaking Challenge `Learner: ___ (→ …)` lines are a practice-UX convention and are",
    "the sole exception.",
    "",
    ...cast.map(personaBlock),
    "---",
    "",
    REQUIRED_PERSONA_SECTIONS[1],
    "",
    "Rules for the planner.",
    "",
    "- Every persona in this file should appear in **≥ 2 units**; if a draft leaves a",
    "  persona unused or under-used, place them in the unit where they fit most naturally.",
    `- A single unit's cast should mix register: at least one ${fill("informal address form")} persona and one`,
    `  ${fill("formal address form")} persona per unit when the unit goal admits register contrast.`,
    "- A persona's first appearance in the curriculum is labeled `(NEW)`; subsequent",
    "  appearances are returning.",
    "- One-off cameo characters (a server, a vendor, a one-time clerk) are allowed; label",
    "  them `(one-off cameo)` so they are not added to the recurring inventory.",
    "- **Scenario archetypes (A1 / A2 / B).** Tag every learning cycle in its heading with",
    "  one archetype: `A1` (in-culture everyday, set in the anchor persona's home region),",
    `  \`A2\` (in-culture transactional/service, same country), or \`B\` (local encounter in ${fill("the diaspora community")}).`,
    "  Target curriculum-wide mix ≈ 25% A1 / 35% A2 / 40% B; every unit covers ≥2",
    "  archetypes; no run of 3+ consecutive units in a single archetype.",
    "- **Settings from `Default scene contexts`.** For an A1 or A2 cycle, pick the city from",
    "  the anchor's `(A1)` / `(A2)` list; for a B cycle, from `(B)`. Persona dialect is fixed",
    "  by origin in all three archetypes.",
    "- **Learner-as-character.** See `## Learner positioning` above.",
    "",
  ].join("\n");

/**
 * `personas.md` for a cast that already has names: the template's file, with one block per current
 * main character of `personas` in place of the four placeholder slots. Every heading then matches a
 * `display_name` in the registry, so the cast gate passes; the prose is still `<<FILL` slots.
 */
export const personasMarkdownFor = (l2: string, personas: readonly Persona[]): string =>
  PERSONAS(
    l2,
    activePersonas({ personas }, "main-character").map((persona) => ({
      name: persona.display_name,
    })),
  );

const exemplarLine = (speaker: string, what: string, answer?: string): string =>
  `- ${speaker}: ${fill(what)}${answer === undefined ? "" : ` (→ ${fill(answer)})`}`;

// Exemplar speakers are cast names, so a template names none: each is a slot for one of the pack's
// own main characters.
const SPEAKER_A = fill("a main character's name");
const SPEAKER_B = fill("another main character's name");

const REFERENCE_DIALOGUES = (): string =>
  [
    "# Reference Dialogues — approved exemplars (style anchors)",
    "",
    `**Source:** ${fill("where these came from: hand-drafted, or extracted from an accepted run")}`,
    "**Use:** style anchors for the exercise-generation step's dialogue authoring — match",
    "their register, rhythm, and naturalness. Content (vocab, gap targets) always follows",
    "the current unit's KV, never these exemplars.",
    "**Cast caveat:** for casting, names, and dialect always follow the current",
    "`personas.md`, never these exemplars.",
    "",
    "<!-- TEMPLATE: one turn per line as `- Speaker: text`. A gap is written as three",
    "underscores in backticks inside the line, and its answer follows as `(→ answer)` at",
    "the end of the line. Speakers are cast names from personas.md, a cameo, or Learner. -->",
    "",
    "## Exemplar 1 — `dialogue_choose_dictate`",
    "",
    exemplarLine(SPEAKER_A, "a line with one `___` gap", "the missing word"),
    exemplarLine(SPEAKER_B, "a reply with one `___` gap", "the missing word"),
    exemplarLine(SPEAKER_A, "a follow-up question"),
    exemplarLine(SPEAKER_B, "a reply with one `___` gap", "the missing word"),
    exemplarLine(SPEAKER_A, "a closing line"),
    "",
    "## Exemplar 2 — `dialogue_choose_item`",
    "",
    exemplarLine(SPEAKER_B, "a greeting and introduction"),
    exemplarLine("Visitor", "a question with one `___` gap", "the missing word or phrase"),
    exemplarLine(SPEAKER_B, "a reply with one `___` gap", "the missing word"),
    exemplarLine("Visitor", "a closing line"),
    "",
    "## Exemplar 3 — `dialogue_speak_default`",
    "",
    exemplarLine(SPEAKER_A, "an opening line setting the scene"),
    `- Learner: ${fill("a reply containing the **bolded target phrase**")}`,
    exemplarLine(SPEAKER_A, "a reaction"),
    `- Learner: ${fill("a second reply with **another bolded target**")}`,
    exemplarLine(SPEAKER_A, "a closing line"),
    "",
  ].join("\n");

// ── Topic files (ADR-0120) ──────────────────────────────────────────────────────
// Each file's keys are the ones the catalog checks require, so a pack built from the template can
// turn every check on once its lists are filled. The other keys a schema allows are optional; the
// header names the schema that lists them. List placeholders are quoted: an unquoted `<<FILL: x>>` is
// a YAML mapping (`: ` inside a plain scalar), not the string the schema needs.

const topicHeader = (what: string, topic: string): string[] => [
  `# ${what}`,
  `# Shape: language-packs/language_packs/schemas/${topic}.schema.json — any other key is refused.`,
  "# Leave out a key you have no data for; an empty list or string is refused, never defaulted.",
];

const fillList = (key: string, what: string): string[] => [
  `${key}:`,
  `  - "${fill(what)}"`,
  `  - "${fill("one entry per line")}"`,
];

const fillMap = (key: string, keyWhat: string, valueWhat: string): string[] => [
  `${key}:`,
  `  "${fill(keyWhat)}": "${fill(valueWhat)}"`,
];

const ORTHOGRAPHY = (): string =>
  [
    ...topicHeader("How this language is written.", "orthography"),
    "",
    "# Characters that decisively mark text as this language rather than the audience L1. One",
    "# string, used verbatim as a character set — not a list, never split or lower-cased.",
    `decisive_chars: "${fill("the characters, no separators")}"`,
    "# Closing mark -> the opening mark it requires (a language without opening marks switches the checks that need them off).",
    ...fillMap("opening_marks", "a closing mark", "its opening mark"),
    "# Unaccented interrogative word -> its accented form, one entry per word.",
    ...fillMap("interrogative_accents", "an unaccented word", "its accented form"),
    "# A whole word spelled wrongly for this variety (a transliterated letter, another variety's spelling) -> its right spelling, one entry per form.",
    ...fillMap("spelling_corrections", "the wrong spelling", "the right spelling"),
    "# Optional. Words or phrases that follow an opening exclamation mark without making the sentence an exclamative.",
    ...fillList("exclamative_exceptions", "a word or phrase, lowercase"),
    "# The letters beyond A-Z, upper and lower case, as two strings.",
    "letters:",
    `  upper: "${fill("upper-case letters beyond A-Z")}"`,
    `  lower: "${fill("lower-case letters beyond a-z")}"`,
    "# Punctuation trimmed off both ends of a form before it is looked up.",
    `strip_chars: "${fill("the characters, no separators")}"`,
    "# Groups of characters that mark text as this language: text with a character from each of two",
    "# groups (or from one group and the function words) reads as this language.",
    "marker_chars:",
    `  - "${fill("one group of characters, no separators")}"`,
    `  - "${fill("another group")}"`,
    "",
  ].join("\n");

const LEXICON = (): string =>
  [
    ...topicHeader("Word lists the checks and fixers read.", "lexicon"),
    "",
    "# Function words that mark a span of text as this language (leaked-L2-prose detection).",
    ...fillList("function_words", "a function word, lowercase"),
    "# Closed-class one-word answers that need not be unit vocabulary (articles, pronouns...).",
    ...fillList("answer_function_words", "a closed-class word, lowercase"),
    "# Words a learner may produce whatever the unit teaches, by group (articles, pronouns,",
    "# prepositions, greetings, common verb forms...), and never flagged as untaught in exercise prose.",
    "closed_class_words:",
    `  "${fill("a group, e.g. articles")}":`,
    `    - "${fill("a word, lowercase")}"`,
    "# Country names, cased, in this language AND in the audience L1.",
    ...fillList("country_names", "a country name"),
    '# Speech verbs that attribute a dialogue line to a speaker ("says"); several words allowed.',
    ...fillList("speaker_prelude_verbs", "a speech verb, lowercase"),
    "# Language and nationality names an explanation may use, in the audience L1. No place names.",
    ...fillList("scene_demonyms", "a language name or demonym, lowercase"),
    "# Phrases that must not leak into an English translation, by label. Several words allowed.",
    "leakage_phrases:",
    `  "${fill("a label, e.g. greeting")}":`,
    `    - "${fill("a phrase, lowercase")}"`,
    "# Weekday and month names, with common unaccented or variant spellings.",
    ...fillList("weekdays", "a weekday name, lowercase"),
    ...fillList("months", "a month name, lowercase"),
    "# Cardinal number words and their values.",
    "number_words:",
    `  "${fill("a number word")}": 0`,
    "# Words in this language that title a closed set such as the alphabet.",
    ...fillList("known_set_titles", "a title word, lowercase"),
    "",
  ].join("\n");

const GRAMMAR = (): string =>
  [
    ...topicHeader("Grammar data the checks and prompts read.", "grammar"),
    "",
    "# Distinct-referent nouns and paradigm forms exempt from the gendered-pair gate",
    "# (grandfather/grandmother: not an agreement pair, the learner needs both).",
    ...fillList("gender_pair_exclude", "a noun or form, lowercase"),
    "# Word forms whose grammatical gender tells the referent's gender (kin, roles, nationalities).",
    ...fillList("gendered_forms", "a gendered form, lowercase"),
    "# Core verb lemmas that must be taught singular-first at A1.1.",
    ...fillList("essential_flagship_verbs", "a verb lemma, lowercase"),
    "# Forms an English gloss cannot tell singular from plural without a qualifier.",
    ...fillList("number_ambiguous_forms", "a form, lowercase"),
    "# Given names the generators emit that are not personas, and role words, with their gender.",
    "name_genders:",
    `  "${fill("a given name, lowercase")}": male`,
    "role_label_genders:",
    `  "${fill("a role or kin word, lowercase")}": female`,
    "# [masculine, feminine] adjective pairs, and the copulas that link a subject to one.",
    "adjective_gender_pairs:",
    `  - ["${fill("masculine form")}", "${fill("feminine form")}"]`,
    ...fillList("copulas", "a copular verb form, lowercase"),
    "# Articles, and contractions with the words they contract.",
    ...fillList("articles", "an article, lowercase"),
    "contractions:",
    `  "${fill("a contraction")}":`,
    `    - "${fill("a word it contracts")}"`,
    "# The patterns behind the person-and-gender agreement checks.",
    "gender_agreement:",
    "  demonstrative_genders:",
    `    "${fill("a demonstrative that introduces a person")}": male`,
    ...[
      "introduction_copulas",
      "negations",
      "possessives",
      "possessive_copulas",
      "relative_complements",
    ].flatMap((key) => [`  ${key}:`, `    - "${fill(key.replaceAll("_", " "))}"`]),
    "  relative_pronoun_genders:",
    `    "${fill("an article that opens a relative clause")}": female`,
    "",
  ].join("\n");

const REGISTER = (): string =>
  [
    ...topicHeader("Forms of address and politeness.", "register"),
    "",
    "# Forms whose English gloss cannot tell informal from formal 'you' without a qualifier, by label.",
    "register_ambiguous_forms:",
    `  "${fill("a label, e.g. informal_formal")}":`,
    `    - "${fill("a form, lowercase")}"`,
    "# Second-person forms by register: which qualifier a gloss of each form gets.",
    "second_person:",
    `  informal: ["${fill("an informal form, lowercase")}"]`,
    `  formal: ["${fill("a formal form, lowercase")}"]`,
    "# Forms that address or refer to more than one person: they make a 'you' or 'they' gloss plural.",
    ...fillList("plural_address", "a form, lowercase"),
    "# Second-person pronouns only (no verb forms), by register, for the mixed-address check. Write a form",
    "# capitalised only when its capital is what marks it formal (a formal pronoun spelled like a",
    "# third-person one): it then counts only capitalised and mid-sentence.",
    "address_pronouns:",
    "  informal:",
    `    - "${fill("an informal pronoun")}"`,
    "  formal:",
    `    - "${fill("a formal pronoun")}"`,
    "# Dialogue formulae, matched in any case. first_meeting belongs near the start or an intro cue;",
    "# farewell in the last two turns; departure_in_progress marks a farewell said while still talking.",
    "formulae:",
    "  first_meeting:",
    `    - "${fill("a first-meeting formula, e.g. nice to meet you")}"`,
    "  farewell:",
    `    - "${fill("a farewell, e.g. goodbye")}"`,
    "  intro_cues:",
    `    - "${fill("a greeting or introduction, e.g. hello, my name is")}"`,
    "  departure_in_progress:",
    `    - "${fill("an announcement of leaving, e.g. I have to go")}"`,
    "guidance: |",
    `  ${fill('the ACTUAL address forms and when each is used — e.g. "informal tu vs. formal Lei"')}`,
    "",
  ].join("\n");

const DIALECT = (): string =>
  [
    ...topicHeader("The variety this pack teaches.", "dialect"),
    "",
    "variety: |",
    `  ${fill("the standard variety taught, and which regional forms are avoided")}`,
    "# Another variety's form -> the form this variety uses, replaced word for word.",
    "substitutions:",
    `  "${fill("a form of another variety, lowercase")}": "${fill("the form this variety uses")}"`,
    "",
  ].join("\n");

const PROMPT_EXAMPLES = (): string =>
  [
    ...topicHeader("Examples the prompts read, by id.", "prompt_examples"),
    "#",
    "# examples:",
    "#   <example_id>: |",
    "#     <example text in this language>",
    "",
  ].join("\n");

// The samples an enabled check needs, commented out: uncomment and fill it for each check set to true.
export const SAMPLES_LINE = "# samples: { pass: [<a text it accepts>], fail: [<a text it flags>] }";

// Every catalog check, with a decision the schema refuses until a human makes it (ADR-0123: there
// are no defaults). The transitional `optional` ones are listed too: a pack created now decides them.
const CHECKS = (): string =>
  [
    "# The check manifest (ADR-0123): this pack decides EVERY check in the catalog.",
    "# Set each `enabled` to true or false — there are no defaults. An enabled check needs the pack",
    "# keys listed with it, and `samples`: texts in this language it accepts (`pass`) and flags",
    "# (`fail`), at least one of each, which every runtime's tests run it on (ADR-0123 Decision 6).",
    "# Once CON-1620 lands, a disabled one never runs for this language.",
    "# Shape: language-packs/language_packs/schemas/checks.schema.json.",
    "",
    "checks:",
    ...CATALOG_CHECK_IDS.flatMap((id) => [
      `  ${id}:`,
      `    # ${CHECK_CATALOG[id].description} Needs: ${CHECK_CATALOG[id].requires.join(", ") || "nothing"}.`,
      `    enabled: "${fill("true or false")}"`,
      ...(isSamplelessCheck(id) ? [] : [`    ${SAMPLES_LINE}`]),
    ]),
    "",
  ].join("\n");

const VOCAB = (l2: string, l1: string): string =>
  [
    "# CEFR vocab reference inventory — inlined into the step-1 prompts, so a pack",
    "# without it fails at step 1. One row per lexeme under `items:`. `CEFR` is the",
    "# major level (A1, A2, B1 …), not a sublevel. `gender` is masculine / feminine /",
    `# neuter, or "" where it does not apply. \`item_${l1}\` is the gloss in the audience L1.`,
    `note: "${fill("where this list comes from and which levels it covers")}"`,
    "items:",
    ...[1, 2].flatMap((n) => [
      `- item_${l2}: "${fill(`lexeme ${n}`)}"`,
      `  item_${l1}: "${fill("gloss")}"`,
      `  part of speech: "${fill("noun / verb / adjective / adverb / preposition")}"`,
      "  CEFR: A1",
      `  gender: "${fill("masculine / feminine / neuter / empty")}"`,
    ]),
    "",
  ].join("\n");

const L1_PROFILE = (l1: string): string =>
  [
    `# L1 profile — L1_${l1} (${displayName(l1)})`,
    "",
    "Audience-L1 conventions, shared across every L2 that this L1 learns.",
    "",
    `- **Display name:** ${displayName(l1)} (from \`language_codes.yml\`).`,
    "- **Gloss language:** all L1 glosses, L1 scene-setting, and `tutor_choose_default`",
    `  rule explanations are written in ${displayName(l1)}. The prompts gloss in the run's`,
    "  L1, so this needs no separate wiring — it is documented here for clarity.",
    "",
    "## Parser configuration",
    "",
    "The match-exercise parser drops L1 pronoun glosses that appear as noise beside L2",
    "forms. The set below is read by `scripts/convert_unit_plan_md_to_json.py` (via",
    "`pipeline.lang.l1_pronoun_noise`). Without it the parser silently falls back to the",
    "ENGLISH set. Lowercase, comma-separated, on one line.",
    "",
    marker("pronoun-noise", "this language's subject pronouns in lowercase"),
    "",
  ].join("\n");

const TUTORS = (l1: string): string =>
  [
    `# Tutoring voices (L1: ${displayName(l1)})`,
    "",
    "The tutors who deliver the spoken L1 explanations in the `tutor_*` exercises",
    "(grammar rules, culture/pragmatic notes). They are an **L1 property**, shared across",
    "every L2 this audience studies — which is why they live in the L1 pack, not in any",
    "L2 `personas.md`.",
    "",
    "They are **not** scene characters: they never appear in dialogues or any L2 content,",
    "and have no register, dialect, origin, or `Default scene contexts` model.",
    "",
    "Each tutor below is a `## <FirstName>` H2 block declaring `**Voice / tone:**` (the",
    "marker the cast gate keys on). Keep the names exactly as written — they are the",
    "`tutor` personas in the sibling `personas.yml`, and any other name is refused.",
    "",
    "---",
    "",
    ...TEMPLATE_TUTORS.map((tutor) => tutor.name).flatMap((name) => [
      `## ${name}`,
      "",
      `- ${TUTOR_VOICE_MARKER} ${fill("temperament and how they explain, e.g. example-first, or names the interference trap before the rule")}`,
      "",
    ]),
  ].join("\n");

/**
 * The skeleton pack for a new language, as `(path, content)` pairs ready to be zipped.
 *
 * The L1 side is included only when an L1 code is given: an L1 pack is shared by every L2 that
 * audience learns and usually exists already. The download also leaves out any file the store
 * already holds (see `templateArchive`), so an existing L1 is never offered for overwriting.
 *
 * `language_codes.yml` carries every known language plus any new code. That is safe to upload
 * because the upload folds registry lines in ADD-ONLY: a code the store already has keeps its
 * stored name.
 */
export const templateLanguagePack = (l2: string, l1?: string): readonly PackFile[] => [
  { path: "language_codes.yml", content: LANGUAGE_CODES(l1 === undefined ? [l2] : [l2, l1]) },
  { path: `L2_${l2}/language_profile.md`, content: LANGUAGE_PROFILE(l2, l1 ?? DEFAULT_L1) },
  { path: `L2_${l2}/personas.md`, content: PERSONAS(l2) },
  { path: `L2_${l2}/personas.yml`, content: personaRegistry(TEMPLATE_CAST, "main-character") },
  { path: `L2_${l2}/reference_dialogues.md`, content: REFERENCE_DIALOGUES() },
  { path: `L2_${l2}/${VOCAB_INVENTORY}`, content: VOCAB(l2, l1 ?? DEFAULT_L1) },
  { path: `L2_${l2}/checks.yml`, content: CHECKS() },
  { path: `L2_${l2}/orthography.yml`, content: ORTHOGRAPHY() },
  { path: `L2_${l2}/lexicon.yml`, content: LEXICON() },
  { path: `L2_${l2}/grammar.yml`, content: GRAMMAR() },
  { path: `L2_${l2}/register.yml`, content: REGISTER() },
  { path: `L2_${l2}/dialect.yml`, content: DIALECT() },
  { path: `L2_${l2}/prompt_examples.yml`, content: PROMPT_EXAMPLES() },
  ...(l1 === undefined
    ? []
    : [
        { path: `L1_${l1}/profile.md`, content: L1_PROFILE(l1) },
        { path: `L1_${l1}/tutors.md`, content: TUTORS(l1) },
        { path: `L1_${l1}/personas.yml`, content: personaRegistry(TEMPLATE_TUTORS, "tutor") },
      ]),
];
